import { State } from '@contexts/cart/cart.reducer';

const CART_STORAGE_KEY = 'danelfeinkost-cart';
const CART_VERSION_KEY = 'danelfeinkost-cart-version';
const CURRENT_CART_VERSION = '1.0';

export interface CartData {
  data: State;
  version: string;
  lastUpdated: string;
}

export function saveCartToStorage(cartData: State): void {
  if (typeof window === 'undefined') return;

  try {
    const cartDataWithMeta: CartData = {
      data: cartData,
      version: CURRENT_CART_VERSION,
      lastUpdated: new Date().toISOString(),
    };

    window.localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify(cartDataWithMeta),
    );
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
}

export function loadCartFromStorage(): State | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return null;

    const cartData: CartData = JSON.parse(stored);

    // Validate cart data structure
    if (!cartData.data || !cartData.data.items) {
      console.warn('Invalid cart data structure, clearing cart');
      clearCartFromStorage();
      return null;
    }

    // Check if cart data is too old (optional: you can set a maximum age)
    const lastUpdated = new Date(cartData.lastUpdated);
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
    if (Date.now() - lastUpdated.getTime() > maxAge) {
      console.warn('Cart data is too old, clearing cart');
      clearCartFromStorage();
      return null;
    }

    return cartData.data;
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    clearCartFromStorage();
    return null;
  }
}

export function clearCartFromStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing cart from localStorage:', error);
  }
}

export function migrateCartData(): void {
  if (typeof window === 'undefined') return;

  try {
    // Check if there's old sessionStorage data to migrate
    const oldSessionData = window.sessionStorage.getItem(CART_STORAGE_KEY);
    if (oldSessionData) {
      const oldCartData = JSON.parse(oldSessionData);

      // Migrate to new format
      const newCartData: CartData = {
        data: oldCartData,
        version: CURRENT_CART_VERSION,
        lastUpdated: new Date().toISOString(),
      };

      // Save to localStorage
      window.localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify(newCartData),
      );

      // Clear old sessionStorage data
      window.sessionStorage.removeItem(CART_STORAGE_KEY);

      console.log('Cart data migrated from sessionStorage to localStorage');
    }
  } catch (error) {
    console.error('Error migrating cart data:', error);
  }
}
